import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/components/common/Icon';
import {
  ASSISTANT_SPOTLIGHT_DURATION_MILLISECONDS,
  ASSISTANT_SPOTLIGHT_PADDING_PIXELS,
} from '@/constants/assistant';
import { useAssistantUi, useAssistantUiState } from './assistantUiContext';

interface SpotlightRectangle {
  top: number;
  left: number;
  width: number;
  height: number;
}

function measure(element: HTMLElement): SpotlightRectangle {
  const bounds = element.getBoundingClientRect();
  return {
    top: bounds.top - ASSISTANT_SPOTLIGHT_PADDING_PIXELS,
    left: bounds.left - ASSISTANT_SPOTLIGHT_PADDING_PIXELS,
    width: bounds.width + ASSISTANT_SPOTLIGHT_PADDING_PIXELS * 2,
    height: bounds.height + ASSISTANT_SPOTLIGHT_PADDING_PIXELS * 2,
  };
}

function isSameLayout(previous: SpotlightRectangle[], next: SpotlightRectangle[]): boolean {
  return (
    previous.length === next.length &&
    previous.every(function matches(rectangle, index) {
      const candidate = next[index];
      return (
        candidate !== undefined &&
        rectangle.top === candidate.top &&
        rectangle.left === candidate.left &&
        rectangle.width === candidate.width &&
        rectangle.height === candidate.height
      );
    })
  );
}

export function SpotlightOverlay() {
  const { clearSpotlight } = useAssistantUi();
  const { spotlight } = useAssistantUiState();
  const [rectangles, setRectangles] = useState<SpotlightRectangle[]>([]);

  useEffect(
    function followSpotlightTargets() {
      if (spotlight === null) {
        setRectangles([]);
        return;
      }
      const elements = spotlight.elements;
      let animationFrame = 0;
      function remeasure() {
        const isAnyTargetGone = elements.some(function isDetached(element) {
          return element.isConnected === false;
        });
        if (isAnyTargetGone) {
          clearSpotlight();
          return;
        }
        const nextRectangles = elements.map(measure);
        setRectangles(function keepWhenUnchanged(previous) {
          return isSameLayout(previous, nextRectangles) ? previous : nextRectangles;
        });
        animationFrame = window.requestAnimationFrame(remeasure);
      }
      remeasure();
      const dismissTimer = window.setTimeout(
        clearSpotlight,
        ASSISTANT_SPOTLIGHT_DURATION_MILLISECONDS
      );
      function dismissOnKey(event: KeyboardEvent) {
        if (event.key === 'Escape') {
          clearSpotlight();
        }
      }
      window.addEventListener('keydown', dismissOnKey);
      window.addEventListener('pointerdown', clearSpotlight);
      return function stopFollowing() {
        window.cancelAnimationFrame(animationFrame);
        window.clearTimeout(dismissTimer);
        window.removeEventListener('keydown', dismissOnKey);
        window.removeEventListener('pointerdown', clearSpotlight);
      };
    },
    [spotlight, clearSpotlight]
  );

  if (spotlight === null || rectangles.length === 0) {
    return null;
  }
  const firstRectangle = rectangles[0];
  return createPortal(
    <div className="fixed inset-0 z-[60] pointer-events-none" aria-hidden="true">
      {rectangles.map(function renderRing(rectangle, index) {
        return (
          <div
            key={index}
            className="absolute rounded-2xl ring-4 ring-zooprimary/30 shadow-[0_0_0_10px_rgb(88_80_160/0.08)] motion-safe:animate-pulse"
            style={{
              top: rectangle.top,
              left: rectangle.left,
              width: rectangle.width,
              height: rectangle.height,
            }}
          />
        );
      })}
      {spotlight.caption === null || firstRectangle === undefined ? null : (
        <div
          className="absolute -translate-y-full -mt-2 max-w-xs bg-zooindigo text-zoowhite text-xs font-semibold px-2.5 py-1.5 rounded-lg shadow-lg flex items-center gap-1.5"
          style={{ top: firstRectangle.top, left: Math.max(firstRectangle.left, 8) }}
        >
          <Icon name="Sparkles" className="shrink-0" />
          {spotlight.caption}
        </div>
      )}
    </div>,
    document.body
  );
}
